import { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router";
import { useNative } from "../../../shared/phone/media";
import { PhoneNav } from "../../../shared/phone/PhoneNav";
import { PnRow } from "../../../shared/phone/Row";
import { Icon } from "../../components/ui/Icon";
import { api } from "../../lib/api";
import { connectionsApi, failing, shownState } from "../../lib/connectionsApi";
import { useOnShown } from "../../lib/inApp";
import { FROM_MORE } from "./parentBack";

/**
 * What needs fixing across every product's connections, that all of them pass, or how many are connected. Read on
 * arrival, and again when the phone app shows the screen again.
 */
function useConnectionSummary(): { words: string; tone: "warn" | "good" | "dim" } | null {
  const [sum, setSum] = useState<{ words: string; tone: "warn" | "good" | "dim" } | null>(null);
  const read = useCallback(async (alive: () => boolean) => {
    try {
      const { products } = await api.products.list();
      const lists = await Promise.all(products.map((p) => connectionsApi.list(p.id).then((r) => r.connections)));
      const conns = lists.flat();
      const fixes = conns.filter((c) => shownState(c) === "fix").reduce((n, c) => n + Math.max(1, failing(c.checks).length), 0);
      const connected = conns.filter((c) => shownState(c) !== "none").length;
      if (!alive()) return;
      if (fixes) setSum({ words: `${fixes} ${fixes === 1 ? "thing" : "things"} to fix`, tone: "warn" });
      // Why: "All verified" only when nothing is left to connect; a product without email sending is not verified.
      else if (connected && connected === conns.length) setSum({ words: "All verified", tone: "good" });
      else if (connected) setSum({ words: `${connected} of ${conns.length} connected`, tone: "dim" });
      else setSum(null);
    } catch {
      // Why: the summary is a hint on a row; the row still opens Connections, which says what failed.
    }
  }, []);
  useEffect(() => {
    let alive = true;
    void read(() => alive);
    return () => {
      alive = false;
    };
  }, [read]);
  useOnShown(() => void read(() => true));
  return sum;
}

/**
 * More, the phone's last tab: what your agent uses. Flashsuite's menu sits beside its title, as on every main
 * screen.
 */
export function MoreView() {
  const native = useNative();
  const navigate = useNavigate();
  const cx = useConnectionSummary();

  // A computer has no More: Flashsuite's menu sits in the top bar.
  if (!native) return <Navigate to="/command" replace />;

  return (
    <div className="pn-screen mo-more">
      <PhoneNav title="More" large />
      <div className="pn-stack">
        <section>
          <div className="pn-cap">What your agent uses</div>
          <div className="pn-grp">
            <PnRow onClick={() => navigate("/connections", { state: FROM_MORE })} icon={<Icon name="plug" />} title="Connections" value={cx ? <span className={`mo-st ${cx.tone}`}>{cx.words}</span> : undefined} chevron />
          </div>
        </section>
      </div>
    </div>
  );
}
